import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { tap } from 'rxjs';
import { API_BASE_URL } from '../http/api-base-url.token';
import { joinUrl } from '../http/url.util';
import { AUTH_LOGIN_ROUTE } from './auth-login-route.token';

export interface LoginResult {
  token: string;
  userId: string;
  email: string;
  role: string;
}

export interface RegisterResult {
  userId: string;
  email: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly apiBaseUrl = inject(API_BASE_URL);
  private readonly loginRoute = inject(AUTH_LOGIN_ROUTE);

  private readonly _token = signal<string | null>(localStorage.getItem('privaci_token'));
  private readonly _role = signal<string | null>(localStorage.getItem('privaci_role'));
  private readonly _email = signal<string | null>(localStorage.getItem('privaci_email'));

  readonly token = this._token.asReadonly();
  readonly role = this._role.asReadonly();
  readonly email = this._email.asReadonly();
  readonly isAuthenticated = computed(() => !!this._token());
  readonly isDomainAdmin = computed(() => this._role() === 'domain_admin');

  login(email: string, password: string) {
    return this.http.post<LoginResult>(joinUrl(this.apiBaseUrl, 'auth/login'), { email, password }).pipe(
      tap(result => this.adoptSession(result.token, result.email, result.role)),
    );
  }

  /**
   * Adopts a session minted by an endpoint other than `auth/login`.
   *
   * Sign-in is not always a login call: completing a team invitation creates the account, grants
   * the access and returns a JWT in one request, and sending that person to a login form
   * afterwards would ask them for the password they just chose. This is the one seam for that —
   * it exists so no caller writes the three storage keys by hand and drifts from `login()`.
   */
  adoptSession(token: string, email: string, role: string): void {
    this._token.set(token);
    this._role.set(role);
    this._email.set(email);
    localStorage.setItem('privaci_token', token);
    localStorage.setItem('privaci_role', role);
    localStorage.setItem('privaci_email', email);
  }

  register(email: string, displayName: string, password: string) {
    return this.http.post<RegisterResult>(joinUrl(this.apiBaseUrl, 'auth/register'), { email, displayName, password });
  }

  verifyEmail(token: string) {
    return this.http.get<{ message: string }>(joinUrl(this.apiBaseUrl, `auth/verify-email?token=${token}`));
  }

  resendVerification(email: string) {
    return this.http.post<{ message: string }>(joinUrl(this.apiBaseUrl, 'auth/resend-verification'), { email });
  }

  requestPasswordReset(email: string) {
    return this.http.post<{ message: string }>(joinUrl(this.apiBaseUrl, 'auth/password-reset-request'), { email });
  }

  confirmPasswordReset(token: string, newPassword: string) {
    return this.http.post<{ message: string }>(joinUrl(this.apiBaseUrl, 'auth/password-reset-confirm'), { token, newPassword });
  }

  logout(): void {
    this._token.set(null);
    this._role.set(null);
    this._email.set(null);
    localStorage.removeItem('privaci_token');
    localStorage.removeItem('privaci_role');
    localStorage.removeItem('privaci_email');
    this.router.navigate(this.loginRoute);
  }
}
